// Schedule draft persistence.
// Drafts: server-side via /api/drafts (Azure Table Storage) so they sync across devices.
// Analysis cache: still IndexedDB (per-device cache, no sharing benefit).

import { msalInstance } from '../authConfig';
import { encodeDraftRequest } from './draftPayload';

const DB_NAME = 'mi_schedule_drafts';
const DB_VERSION = 1;
const STORE_DRAFTS = 'drafts';
const STORE_ANALYSIS = 'analysis';
const MIGRATION_FLAG = 'mi_drafts_migrated_to_azure';

const API_BASE = '/api/drafts';

// --- Local development fallback -------------------------------------------
// In production, drafts live server-side via /api/drafts (Azure Table Storage).
// That Azure Function does NOT run under `npm start`, so during local dev we
// persist drafts to the browser's IndexedDB instead (the STORE_DRAFTS object
// store defined below). IndexedDB is used rather than localStorage because a
// full year of sessions easily exceeds localStorage's ~5MB quota. This lets
// the whole generator + revenue-report flow be tested locally with `npm start`
// alone, with NO Azure Functions / storage-connection setup required.
//
// Gated strictly to development: production builds (NODE_ENV==='production')
// never touch this path, so live behaviour is completely unchanged.
const DEV_LOCAL_DRAFTS = process.env.NODE_ENV === 'development';

const idbSaveDraft = async (draft) => {
  const now = new Date().toISOString();
  const existing = await idbGetDraft(draft.id);
  const merged = {
    ...draft,
    createdAt: draft.createdAt || existing?.createdAt || now,
    updatedAt: now
  };
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_DRAFTS, 'readwrite');
    tx.objectStore(STORE_DRAFTS).put(merged);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  return merged;
};

const idbGetDraft = async (id) => {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_DRAFTS, 'readonly');
      const request = tx.objectStore(STORE_DRAFTS).get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (_) {
    return null;
  }
};

const idbDeleteDraft = async (id) => {
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_DRAFTS, 'readwrite');
    tx.objectStore(STORE_DRAFTS).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// --- Low-level IndexedDB (kept for analysis cache + one-time migration) ---

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        const draftStore = db.createObjectStore(STORE_DRAFTS, { keyPath: 'id' });
        draftStore.createIndex('targetYear', 'targetYear', { unique: false });
        draftStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORE_ANALYSIS)) {
        db.createObjectStore(STORE_ANALYSIS, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// --- API helper ---

// Acquire a Microsoft Graph access token via MSAL. The drafts function
// validates this token against Graph to identify the user and check their
// AAD security group membership — same model as authenticatedDataverseService.
//
// Note: we ask for fully-qualified Graph scopes to make sure MSAL never hands
// back a token issued for the app's own audience (which would cause Graph to
// return "Signing key is invalid" / InvalidAuthenticationToken on the server).
const GRAPH_SCOPES = [
  'https://graph.microsoft.com/User.Read',
  'https://graph.microsoft.com/GroupMember.Read.All'
];

const summarizeJwt = (token) => {
  try {
    const [, payload] = token.split('.');
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return { aud: claims.aud, scp: claims.scp, iss: claims.iss, tid: claims.tid, exp: claims.exp, len: token.length };
  } catch (_) {
    return { len: token?.length || 0, parseable: false };
  }
};

const getGraphToken = async () => {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error('Not signed in. Please sign in to access drafts.');
  }
  const request = { scopes: GRAPH_SCOPES, account };
  let result;
  try {
    result = await msalInstance.acquireTokenSilent(request);
  } catch (e) {
    // Silent failed — likely interaction required (consent or expired refresh
    // token). Fall back to popup so the user can re-authenticate.
    try {
      result = await msalInstance.acquireTokenPopup(request);
    } catch (popupErr) {
      throw new Error(`Could not acquire Graph token: ${popupErr.message || popupErr}`);
    }
  }
  // Diagnostic: log the token's audience/scope so we can confirm it really is
  // a Graph token. Remove once stable.
  console.debug('[drafts] Graph token claims:', summarizeJwt(result.accessToken));
  return result.accessToken;
};

const apiRequest = async (path, options = {}) => {
  const url = `${API_BASE}${path}`;
  let token;
  try {
    token = await getGraphToken();
  } catch (authErr) {
    throw new Error(`Drafts API: ${authErr.message}`);
  }

  let res;
  try {
    const { headers: callerHeaders, ...rest } = options;
    res = await fetch(url, {
      ...rest,
      headers: {
        'Content-Type': 'application/json',
        ...callerHeaders,
        // Send the Graph token in a custom header AND as Bearer. Azure Static
        // Web Apps' linked-Function proxy has been observed to strip/rewrite
        // the Authorization header in some configurations; the custom header
        // is a safe fallback the platform leaves alone.
        'x-graph-access-token': token,
        Authorization: `Bearer ${token}`
      }
    });
  } catch (netErr) {
    throw new Error(`Cannot reach ${url}. The /api/drafts function may not be deployed, or there is a network issue. [details: ${netErr.message}]`);
  }
  if (res.status === 204) return null;
  const text = await res.text();
  // Detect HTML response (means request didn't reach the function — SWA returned the SPA fallback)
  if (text && text.trim().startsWith('<')) {
    throw new Error(`API endpoint returned HTML instead of JSON. The /api/drafts function is likely not deployed. URL: ${url}`);
  }
  let data = null;
  try { data = text ? JSON.parse(text) : null; }
  catch (e) {
    throw new Error(`Drafts API returned non-JSON response (HTTP ${res.status}): ${text.slice(0, 100)}`);
  }
  if (!res.ok) {
    const msg = (data && data.error) || `HTTP ${res.status}`;
    const detail = data?.message ? ` — ${data.message}` : '';
    throw new Error(`Drafts API: ${msg}${detail}`);
  }
  return data;
};

// --- Draft operations (server-backed) ---

export const saveDraft = async (draft) => {
  if (!draft.id) draft.id = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  if (DEV_LOCAL_DRAFTS) return idbSaveDraft(draft);
  return apiRequest('', {
    method: 'POST',
    body: await encodeDraftRequest(draft)
  });
};

export const getDraft = async (id) => {
  if (DEV_LOCAL_DRAFTS) return idbGetDraft(id);
  try {
    return await apiRequest(`/${encodeURIComponent(id)}`);
  } catch (e) {
    if (/HTTP 404|Not found/i.test(e.message)) return null;
    throw e;
  }
};

export const getAllDrafts = async () => {
  if (DEV_LOCAL_DRAFTS) {
    const drafts = await readLocalDrafts();
    return drafts.sort((a, b) =>
      new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
    );
  }
  // Run migration once on first call after deploy
  await migrateLocalDraftsIfNeeded();
  const drafts = await apiRequest('');
  return Array.isArray(drafts) ? drafts : [];
};

export const getDraftsByYear = async (targetYear) => {
  const all = await getAllDrafts();
  return all.filter(d => d.targetYear === targetYear);
};

export const deleteDraft = async (id) => {
  if (DEV_LOCAL_DRAFTS) {
    await idbDeleteDraft(id);
    return;
  }
  await apiRequest(`/${encodeURIComponent(id)}`, { method: 'DELETE' });
};

// --- One-time migration: upload local IndexedDB drafts to Azure ---

const migrateLocalDraftsIfNeeded = async () => {
  try {
    if (localStorage.getItem(MIGRATION_FLAG) === 'done') return;

    const localDrafts = await readLocalDrafts();
    if (localDrafts.length === 0) {
      localStorage.setItem(MIGRATION_FLAG, 'done');
      return;
    }

    // Pull existing server drafts so we don't clobber newer server-side edits
    let serverDrafts = [];
    try {
      const data = await apiRequest('');
      serverDrafts = Array.isArray(data) ? data : [];
    } catch (e) {
      console.warn('Migration: failed to read server drafts, will retry later', e);
      return;
    }
    const serverIds = new Set(serverDrafts.map(d => d.id));

    let migrated = 0;
    for (const draft of localDrafts) {
      if (serverIds.has(draft.id)) continue;
      try {
        await apiRequest('', { method: 'POST', body: await encodeDraftRequest(draft) });
        migrated++;
      } catch (e) {
        console.warn('Migration: failed to upload draft', draft.id, e);
      }
    }
    console.info(`Drafts migration complete: uploaded ${migrated} of ${localDrafts.length} local drafts`);
    localStorage.setItem(MIGRATION_FLAG, 'done');
  } catch (e) {
    console.warn('Drafts migration skipped:', e);
  }
};

const readLocalDrafts = async () => {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_DRAFTS, 'readonly');
      const request = tx.objectStore(STORE_DRAFTS).getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (e) {
    return [];
  }
};

// --- Analysis cache operations (still IndexedDB; per-device cache) ---

const ANALYSIS_CACHE_VERSION = 7;

export const saveAnalysisCache = async (data) => {
  const db = await openDB();
  const record = {
    id: 'latest_analysis',
    data,
    cachedAt: new Date().toISOString(),
    version: ANALYSIS_CACHE_VERSION
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ANALYSIS, 'readwrite');
    tx.objectStore(STORE_ANALYSIS).put(record);
    tx.oncomplete = () => resolve(record);
    tx.onerror = () => reject(tx.error);
  });
};

export const getAnalysisCache = async (maxAgeMinutes = 60) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ANALYSIS, 'readonly');
    const request = tx.objectStore(STORE_ANALYSIS).get('latest_analysis');
    request.onsuccess = () => {
      const record = request.result;
      if (!record) return resolve(null);
      if (record.version !== ANALYSIS_CACHE_VERSION) return resolve(null);
      if (!record.data || !Array.isArray(record.data.sessions) || record.data.sessions.length === 0) {
        return resolve(null);
      }
      const cachedAt = new Date(record.cachedAt);
      const ageMinutes = (Date.now() - cachedAt.getTime()) / (1000 * 60);
      if (ageMinutes > maxAgeMinutes) return resolve(null);
      resolve({
        ...record.data,
        analysisCache: {
          cachedAt: record.cachedAt,
          source: 'browser-cache'
        }
      });
    };
    request.onerror = () => reject(request.error);
  });
};

// --- Export / Import for sharing drafts between managers ---

export const exportDraftToJSON = async (id) => {
  const draft = await getDraft(id);
  if (!draft) return null;
  return JSON.stringify(draft, null, 2);
};

export const importDraftFromJSON = async (jsonString) => {
  try {
    const draft = JSON.parse(jsonString);
    if (!draft.id) draft.id = `imported-${Date.now()}`;
    draft.importedAt = new Date().toISOString();
    return await saveDraft(draft);
  } catch (error) {
    console.error('Failed to import draft:', error);
    return null;
  }
};
